<template>
  <div class="city-autocomplete">
    <input
      ref="inputEl"
      v-model="query"
      type="text"
      class="city-autocomplete__input"
      :class="{ 'city-autocomplete__input--invalid': showInvalid }"
      :placeholder="placeholder"
      autocomplete="off"
      @focus="onFocus"
      @blur="onBlur"
      @keydown.down.prevent="moveActive(1)"
      @keydown.up.prevent="moveActive(-1)"
      @keydown.enter.prevent="selectActive"
      @keydown.escape="close"
    />
    <ul v-if="isOpen && suggestions.length > 0" class="city-autocomplete__list">
      <li
        v-for="(city, index) in suggestions"
        :key="city.id"
        class="city-autocomplete__item"
        :class="{ 'city-autocomplete__item--active': index === activeIndex }"
        @mousedown.prevent="pick(city)"
      >
        {{ city.display_name }}
      </li>
    </ul>
    <ul v-else-if="isOpen && query.trim().length >= 2 && !loading" class="city-autocomplete__list">
      <li class="city-autocomplete__item city-autocomplete__item--empty">Город не найден</li>
    </ul>
    <p v-if="showInvalid" class="city-autocomplete__hint">Выберите город из списка</p>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { citiesApi, type CitySuggestion } from '@/modules/order/api/cities.api'

const props = withDefaults(
  defineProps<{
    modelValue: string
    cityId?: number | null
    placeholder?: string
  }>(),
  {
    placeholder: 'Начните вводить город, например: Москва',
    cityId: null,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'select', city: CitySuggestion | null): void
}>()

const query = ref(props.modelValue)
const suggestions = ref<CitySuggestion[]>([])
const isOpen = ref(false)
const loading = ref(false)
const activeIndex = ref(-1)
const touched = ref(false)
const inputEl = ref<HTMLInputElement | null>(null)
const selectedCityName = ref<string | null>(props.cityId !== null ? props.modelValue : null)

let debounceTimer: ReturnType<typeof setTimeout> | undefined
let requestToken = 0

watch(
  () => props.modelValue,
  (value) => {
    if (value !== query.value) query.value = value
  },
)

watch(
  () => props.cityId,
  (value) => {
    if (value === null) {
      selectedCityName.value = null
    } else if (selectedCityName.value === null) {
      selectedCityName.value = query.value
    }
  },
)

watch(query, (value) => {
  emit('update:modelValue', value)

  // Изменение текста после выбора сбрасывает город, но изменение query внутри pick()
  // не должно немедленно инвалидировать только что выбранное значение.
  if (selectedCityName.value !== value) {
    selectedCityName.value = null
    emit('select', null)
  }

  touched.value = true
  activeIndex.value = -1

  if (debounceTimer) clearTimeout(debounceTimer)
  const trimmed = value.trim().replace(/[,.!?;:]+$/u, '').trim()
  if (trimmed.length < 2) {
    requestToken++
    suggestions.value = []
    loading.value = false
    return
  }

  // Пока ждём debounce и ответ, список не должен показывать «Город не найден»
  loading.value = true
  debounceTimer = setTimeout(() => runSearch(trimmed), 300)
})

async function runSearch(text: string) {
  const token = ++requestToken
  loading.value = true
  try {
    const result = await citiesApi.search(text)
    if (token !== requestToken) return
    suggestions.value = result
  } catch {
    if (token === requestToken) suggestions.value = []
  } finally {
    if (token === requestToken) loading.value = false
  }
}

function onFocus() {
  isOpen.value = true
}

function onBlur() {
  // Небольшая задержка, чтобы успел отработать @mousedown на пункте списка.
  setTimeout(close, 150)
}

function close() {
  isOpen.value = false
}

function moveActive(direction: number) {
  if (!isOpen.value || suggestions.value.length === 0) return
  const next = activeIndex.value + direction
  activeIndex.value = Math.max(0, Math.min(suggestions.value.length - 1, next))
}

function selectActive() {
  const city = suggestions.value[activeIndex.value]
  if (city) pick(city)
}

function pick(city: CitySuggestion) {
  selectedCityName.value = city.display_name
  query.value = city.display_name
  suggestions.value = []
  close()
  emit('select', city)
  inputEl.value?.blur()
}

const showInvalid = computed(() => touched.value && query.value.trim() !== '' && selectedCityName.value === null)
</script>

<style scoped>
.city-autocomplete {
  position: relative;
}

.city-autocomplete__input {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  width: 100%;
  font: inherit;
  box-sizing: border-box;
}

.city-autocomplete__input--invalid {
  border-color: #f59e0b;
}

.city-autocomplete__list {
  position: absolute;
  z-index: 20;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  max-height: 240px;
  overflow-y: auto;
}

.city-autocomplete__item {
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.city-autocomplete__item:hover,
.city-autocomplete__item--active {
  background: #eff6ff;
  color: var(--color-accent);
}

.city-autocomplete__item--empty {
  color: #94a3b8;
  cursor: default;
}

.city-autocomplete__item--empty:hover {
  background: none;
  color: #94a3b8;
}

.city-autocomplete__hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #b45309;
}
</style>
